import { useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import type { ContentType, SearchParams, SearchScope } from "../../api/types";
import { search } from "../../api/content";
import { useSearch } from "../../hooks/useSearch";
import { useCollections, separateCollections } from "../../hooks/useCollections";
import { Loading, EmptyState, ErrorState } from "../Spinner";
import { SearchBar } from "./SearchBar";
import { SortControl } from "./SortControl";
import { Pagination } from "./Pagination";
import { Tag } from "../Tag";
import { ContactFrame } from "../media/ContactFrame";
import { accession, formatDate, formatYear } from "../../lib/format";

/** Page size — mirrors the one used inside the useSearch hook. */
const PAGE_SIZE = 12;

interface MediaSearchGalleryProps {
  /** Lock the search to these content types; omit for every type. */
  types?: ContentType[];
  /** Lock the search to these collections; omit for every collection. */
  collections?: string[];
  /**
   * "MAIN" keeps the separate collections out of the listing — what the Photos
   * and Videos galleries want. Omit (or "ALL") to search everything.
   */
  scope?: SearchScope;
  /** Singular noun for the count line, e.g. "photo" / "video" / "item". */
  noun: string;
  placeholder?: string;
  emptyLabel?: string;
  /** Quick category tabs that set the tag filter. `tag: null` means "All". */
  categories?: { label: string; tag: string | null }[];
}

/**
 * A faceted contact-sheet gallery: search box, category tabs, tag and year
 * filters, sort, a live hit count, and pagination. Used by the Photos and
 * Videos pages and by every collection's own section page — they differ only in
 * what they lock the query to.
 *
 * The interesting behaviour is the scope. A collection marked `separate` (the
 * scuba library, which will run to thousands of frames) would otherwise bury
 * everything else in the gallery and dominate the tag facet. Passing
 * `scope="MAIN"` leaves those collections out and shows a line saying exactly
 * how much is being held back and where to find it — hidden from the default
 * listing, never from search.
 */
export function MediaSearchGallery({
  types,
  collections,
  scope,
  noun,
  placeholder,
  emptyLabel,
  categories,
}: MediaSearchGalleryProps) {
  // Callers pass these as literals, so re-derive stable arrays from their
  // contents. Without this the fetcher identity would change on every render
  // and the search would refetch forever.
  const typeKey = (types ?? []).join(",");
  const collectionKey = (collections ?? []).join(",");
  const lockedTypes = useMemo(
    () => (typeKey ? (typeKey.split(",") as ContentType[]) : undefined),
    [typeKey],
  );
  const lockedCollections = useMemo(
    () => (collectionKey ? collectionKey.split(",") : undefined),
    [collectionKey],
  );

  // Force the type/collection/scope on every request; the rest of the params
  // (query, tags, year, sort, page) come from the shared hook.
  const fetcher = useCallback(
    (params: SearchParams) =>
      search({
        ...params,
        types: lockedTypes ?? params.types,
        collections: lockedCollections ?? params.collections,
        scope,
      }),
    [lockedTypes, lockedCollections, scope],
  );

  const {
    query,
    tags,
    year,
    sort,
    page,
    response,
    loading,
    error,
    setQuery,
    toggleTag,
    setTags,
    setYear,
    setSort,
    setPage,
    clearFilters,
  } = useSearch(fetcher, { initialSort: "MIXED" });

  const total = response?.totalItems ?? 0;
  const items = response?.items ?? [];
  const byTag = response?.aggregations?.byTag ?? [];
  const byYear = response?.aggregations?.byYear ?? [];
  const byCollection = response?.aggregations?.byCollection ?? [];

  const { collections: known } = useCollections();

  /**
   * The collections this listing is deliberately leaving out, with how much is
   * in each. Read from the collection facet, which reports them even while they
   * are excluded — that is the whole point of it.
   */
  const heldBack = useMemo(() => {
    if (scope !== "MAIN") return [];
    return separateCollections(known)
      .map((collection) => ({
        collection,
        count: byCollection.find((f) => f.value === collection.id)?.count ?? 0,
      }))
      .filter((entry) => entry.count > 0);
  }, [scope, known, byCollection]);

  const hasFilters = tags.length > 0 || year !== null;

  /** A category is active when it's the only selected tag (or "All" = none). */
  function isActiveCategory(tag: string | null): boolean {
    return tag === null ? tags.length === 0 : tags.length === 1 && tags[0] === tag;
  }

  return (
    <div className="stack" style={{ gap: "var(--space-4)" }}>
      {categories && categories.length > 0 && (
        <div className="cat-tabs" role="tablist" aria-label="Categories">
          {categories.map((cat) => (
            <button
              key={cat.label}
              type="button"
              role="tab"
              aria-selected={isActiveCategory(cat.tag)}
              className="cat-tab"
              data-active={isActiveCategory(cat.tag)}
              onClick={() => setTags(cat.tag ? [cat.tag] : [])}
            >
              {cat.label}
            </button>
          ))}
        </div>
      )}

      {heldBack.length > 0 && (
        <p className="scope-note">
          {heldBack.map(({ collection, count }) => (
            <span key={collection.id}>
              <b>{count}</b> {count === 1 ? noun : `${noun}s`} live in{" "}
              <Link to={`/collections/${collection.id}`}>{collection.label}</Link>, which has
              its own section so it doesn't crowd out everything else.
            </span>
          ))}{" "}
          Site search still covers all of it.
        </p>
      )}

      <SearchBar value={query} onChange={setQuery} placeholder={placeholder} />

      <div className="search-shell">
        <aside className="facets" aria-label="Filters">
          <div className="facets__group">
            <p className="facets__legend">
              <span>Tags</span>
              {hasFilters && (
                <button
                  type="button"
                  className="searchbar__clear"
                  style={{ padding: 0, fontSize: "0.66rem" }}
                  onClick={clearFilters}
                >
                  Reset
                </button>
              )}
            </p>
            {byTag.length === 0 ? (
              <p className="facet-row__count">No tags to filter.</p>
            ) : (
              <div className="facets__tags">
                {byTag.map((facet) => (
                  <Tag
                    key={facet.value}
                    label={facet.value}
                    count={facet.count}
                    active={tags.includes(facet.value)}
                    onClick={() => toggleTag(facet.value)}
                  />
                ))}
              </div>
            )}
          </div>

          {byYear.length > 1 && (
            <div className="facets__group">
              <p className="facets__legend">
                <span>Year</span>
              </p>
              <div className="facets__tags">
                {byYear.map((facet) => (
                  <Tag
                    key={facet.value}
                    label={facet.value}
                    count={facet.count}
                    active={year === facet.value}
                    onClick={() => setYear(year === facet.value ? null : facet.value)}
                  />
                ))}
              </div>
            </div>
          )}
        </aside>

        <div>
          <div className="results-meta">
            <span className="results-meta__count">
              <b>{total}</b> {total === 1 ? noun : `${noun}s`}
              {query ? ` for “${query}”` : ""}
              {year ? ` from ${year}` : ""}
            </span>
            <SortControl value={sort} onChange={setSort} />
          </div>

          {error ? (
            <ErrorState label={error} />
          ) : loading && items.length === 0 ? (
            <Loading label="Searching…" />
          ) : items.length === 0 ? (
            <EmptyState label={emptyLabel ?? `No ${noun}s match your search.`} />
          ) : (
            <>
              <div className="sheet" aria-busy={loading}>
                {items.map((item, i) => (
                  <ContactFrame
                    key={item.id}
                    index={page * PAGE_SIZE + i + 1}
                    code={accession(item.type, item.sourceId)}
                    title={item.title}
                    caption={
                      (item.type === "PHOTO"
                        ? formatYear(item.occurredAt)
                        : formatDate(item.occurredAt)) || undefined
                    }
                    imageUrl={item.thumbnailUrl}
                    isVideo={item.type === "VIDEO"}
                    to={`/documents/${item.id}`}
                  />
                ))}
              </div>
              <Pagination
                page={page}
                totalPages={response?.totalPages ?? 1}
                onChange={setPage}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
